import { useEffect, RefObject, MutableRefObject, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import type { RestaurantWithEventSpaces } from '@/types/restaurant';
import { createMapMarker } from './MapMarker';

export const useMapMarkers = (
  map: MutableRefObject<mapboxgl.Map | null>,
  restaurants: RestaurantWithEventSpaces[],
  markers: MutableRefObject<mapboxgl.Marker[]>,
  isLoading: boolean,
  clearMarkers: () => void
) => {
  // Memoize valid marker data to prevent unnecessary recalculations
  const markersData = useMemo(() => {
    return restaurants
      .filter(restaurant => {
        const hasCoords = restaurant.latitude && restaurant.longitude;
        if (!hasCoords) {
          console.warn(`Missing coordinates for restaurant: ${restaurant.name}`);
        }
        return hasCoords;
      })
      .map(restaurant => ({
        restaurant,
        coordinates: [Number(restaurant.longitude), Number(restaurant.latitude)] as [number, number]
      }));
  }, [restaurants]);

  useEffect(() => {
    console.log('useMapMarkers effect running:', {
      mapExists: !!map.current,
      isLoading,
      restaurantsCount: restaurants.length
    });

    if (!map.current || isLoading) {
      console.log('Skipping markers update:', {
        reason: !map.current ? 'no map instance' : 'map is still loading'
      });
      return;
    }

    let isMounted = true;
    const currentMap = map.current;

    const addMarkers = async () => {
      if (!isMounted || !currentMap) return;

      // Clear existing markers
      clearMarkers();
      console.log('Cleared existing markers');

      // Create bounds object for fitting markers
      const bounds = new mapboxgl.LngLatBounds();

      // Extend bounds in a single pass
      markersData.forEach(({ coordinates }) => {
        bounds.extend(coordinates);
      });

      // Create markers in larger batches for better performance
      const BATCH_SIZE = 10;
      const newMarkers: mapboxgl.Marker[] = [];

      for (let i = 0; i < markersData.length; i += BATCH_SIZE) {
        if (!isMounted || !currentMap) break;

        const batch = markersData.slice(i, i + BATCH_SIZE);
        
        batch.forEach(({ restaurant, coordinates }) => {
          try {
            const marker = createMapMarker(restaurant, currentMap, coordinates);
            newMarkers.push(marker);
          } catch (error) {
            console.error('Error creating marker:', error);
          }
        });

        // Larger delay between batches to prevent UI blocking
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      if (!isMounted || !currentMap) return;
      
      markers.current = newMarkers;

      // Adjust map view if there are markers
      if (newMarkers.length > 0) {
        console.log('Adjusting map view for markers:', newMarkers.length);
        
        try {
          if (newMarkers.length > 1) {
            // Calculate the optimal zoom level based on bounds
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const boundsWidth = Math.abs(ne.lng - sw.lng);
            const boundsHeight = Math.abs(ne.lat - sw.lat);
            
            // Dynamic padding based on bounds size and marker count
            const basePadding = 50;
            const scaleFactor = Math.min(1.5, Math.max(0.8, 
              Math.log(newMarkers.length) / Math.log(10)
            ));
            const padding = Math.min(
              200,
              Math.max(
                basePadding,
                (boundsWidth + boundsHeight) * 50 * scaleFactor
              )
            );

            currentMap.fitBounds(bounds, {
              padding,
              maxZoom: 15,
              duration: 1000,
              essential: true
            });
          } else {
            const marker = newMarkers[0];
            const coordinates = marker.getLngLat();
            
            currentMap.easeTo({
              center: coordinates,
              zoom: 14.5,
              duration: 1000,
              essential: true
            });
          }
          console.log('Map view adjusted successfully');
        } catch (error) {
          console.error('Error adjusting map view:', error);
        }
      } else {
        currentMap.easeTo({
          center: [-73.935242, 40.730610],
          zoom: 12,
          duration: 1000,
          essential: true
        });
        console.log('No markers added, centering on NYC');
      }
    };

    // Start adding markers
    addMarkers();

    return () => {
      console.log('Cleaning up markers');
      isMounted = false;
      clearMarkers();
    };
  }, [map, markersData, markers, isLoading, clearMarkers]);
};